import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Toast,
  ToastToggle,
} from "flowbite-react";
import { HiHome, HiCheck, HiX } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  getAllInvites,
  acceptInvite,
  rejectInvite,
  InviteStatus,
  type UserMembershipInvite,
} from "../../services/invites";
import { blueThemeSpinner } from "../exercises/ExerciseModal";

const InvitesPage: FC = function () {
  const [invites, setInvites] = useState<UserMembershipInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<
    "accept" | "reject" | null
  >(null);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | null>(null);

  const showToast = (message: string, type: "success" | "error") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastType(null);
      setToastMessage("");
    }, 5000);
  };

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const response = await getAllInvites();
      setInvites(response.results);
    } catch (error) {
      console.error("Failed to fetch invites:", error);
      showToast("Failed to load invites", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleAccept = async (invite: UserMembershipInvite) => {
    try {
      setProcessingId(invite.id);
      setProcessingAction("accept");
      await acceptInvite(invite.id);
      showToast("Invite accepted successfully", "success");
      setInvites((prev) =>
        prev.map((i) =>
          i.id === invite.id ? { ...i, status: InviteStatus.ACCEPTED } : i,
        ),
      );
    } catch (error) {
      console.error("Failed to accept invite:", error);
      showToast("Failed to accept invite", "error");
    } finally {
      setProcessingId(null);
      setProcessingAction(null);
    }
  };

  const handleReject = async (invite: UserMembershipInvite) => {
    try {
      setProcessingId(invite.id);
      setProcessingAction("reject");
      await rejectInvite(invite.id);
      showToast("Invite rejected successfully", "success");
      setInvites((prev) =>
        prev.map((i) =>
          i.id === invite.id ? { ...i, status: InviteStatus.REJECTED } : i,
        ),
      );
    } catch (error) {
      console.error("Failed to reject invite:", error);
      showToast("Failed to reject invite", "error");
    } finally {
      setProcessingId(null);
      setProcessingAction(null);
    }
  };

  const isProcessingAccept = (inviteId: string) =>
    processingId === inviteId && processingAction === "accept";
  const isProcessingReject = (inviteId: string) =>
    processingId === inviteId && processingAction === "reject";

  const getStatusBadge = (status: InviteStatus) => {
    switch (status) {
      case InviteStatus.PENDING:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100">
            Pending
          </span>
        );
      case InviteStatus.ACCEPTED:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
            Accepted
          </span>
        );
      case InviteStatus.REJECTED:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100">
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="p-4">
        <div className="max-w-full">
          <div className="mb-3">
            <Breadcrumb className="mb-3">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem>Membership</BreadcrumbItem>
              <BreadcrumbItem href="/membership/invites">
                Invites
              </BreadcrumbItem>
            </Breadcrumb>
          </div>
          <div className="mb-4">
            <div className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl mb-4">
              Membership Invites
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center p-6">
              <Spinner theme={blueThemeSpinner} color="info" size="lg" />
              <span className="ml-3 text-gray-500 dark:text-gray-400">
                Loading invites...
              </span>
            </div>
          ) : (
            <Card className="p-4">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    All Invites
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Review and manage membership invite requests.
                  </p>
                </div>

                {invites.length === 0 ? (
                  <div className="text-center p-6">
                    <p className="text-gray-500 dark:text-gray-400">
                      No invites found.
                    </p>
                  </div>
                ) : (
                  <div className="max-w-7xl" style={{ overflowX: "scroll" }}>
                    <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                      <TableHead className="bg-gray-100 dark:bg-gray-700">
                        <TableHeadCell className="w-full">
                          Full Name
                        </TableHeadCell>
                        <TableHeadCell className="w-full">Email</TableHeadCell>
                        <TableHeadCell className="w-full">
                          Referral
                        </TableHeadCell>
                        <TableHeadCell className="whitespace-nowrap">
                          Date Applied
                        </TableHeadCell>
                        <TableHeadCell>Status</TableHeadCell>
                        <TableHeadCell className="whitespace-nowrap">
                          Actions
                        </TableHeadCell>
                      </TableHead>
                      <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                        {invites.map((invite) => (
                          <TableRow
                            key={invite.id}
                            className="hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <TableCell className="whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white">
                              {invite.full_name}
                            </TableCell>
                            <TableCell className="whitespace-nowrap px-6 py-4 text-gray-500 dark:text-gray-400">
                              {invite.email}
                            </TableCell>
                            <TableCell className="whitespace-nowrap px-6 py-4 text-gray-500 dark:text-gray-400">
                              {invite.referral || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap px-6 py-4 text-gray-500 dark:text-gray-400">
                              {new Date(invite.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap px-6 py-4">
                              {getStatusBadge(invite.status)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap px-6 py-4">
                              {invite.status === InviteStatus.PENDING ? (
                                <div className="flex items-center gap-x-3">
                                  <Button
                                    size="xs"
                                    color="primary"
                                    onClick={() => handleAccept(invite)}
                                    disabled={processingId === invite.id}
                                  >
                                    {isProcessingAccept(invite.id) ? (
                                      <>
                                        <Spinner size="xs" className="mr-2" />
                                        Accepting...
                                      </>
                                    ) : (
                                      <>
                                        <HiCheck className="mr-1 size-4" />
                                        Accept
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="xs"
                                    color="failure"
                                    onClick={() => handleReject(invite)}
                                    disabled={processingId === invite.id}
                                  >
                                    {isProcessingReject(invite.id) ? (
                                      <>
                                        <Spinner size="xs" className="mr-2" />
                                        Rejecting...
                                      </>
                                    ) : (
                                      <>
                                        <HiX className="mr-1 size-4" />
                                        Reject
                                      </>
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {toastType && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast>
            <div
              className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                toastType === "success"
                  ? "bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200"
                  : "bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200"
              }`}
            >
              {toastType === "success" ? (
                <HiCheck className="h-5 w-5" />
              ) : (
                <HiX className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3 text-sm font-normal">{toastMessage}</div>
            <ToastToggle onDismiss={() => setToastType(null)} />
          </Toast>
        </div>
      )}
    </NavbarSidebarLayout>
  );
};

export default InvitesPage;
